import React from "react";
//Material imports
import { ThemeProvider } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";

//Limitless imports
import { useExpertProfileContext, useExpertRatingContext } from "@limitless/expert";
import { usePageViewRecorder } from "@limitless/analytics";
import { modernTheme } from "@limitless/themeing/modern-theme/ModernTheme";
import Box from "@limitless/modern/layout/Box";
import Paper from "@limitless/modern/surfaces/paper/Paper";
import Award from "@limitless/icons/Award";
import TrendingUp from "@limitless/icons/TrendingUp";
import RatingDashboardHeader from "../components/rating-dashboard/RatingDashboardHeader";
import RatingScore from "../components/rating-dashboard/RatingScore";
import LearnMoreSection from "../components/rating-dashboard/LearnMoreSection";
import RatingTable from "../components/rating-dashboard/RatingTable";

const useStyles = makeStyles(() => ({
    icon: {
        width: "72px",
        height: "72px",
        opacity: 0.5,
        color: theme => theme.palette.primary.main
    },
    container: {
        backgroundColor: "transparent",
        display: "grid",
        height: "fit-content",
        marginBottom: "24px",
        marginTop: "16px",
        gridAutoColumns: "1fr",
        gridTemplateColumns: "28fr 72fr",
        gridTemplateRows: "1fr auto auto",
        gap: "16px 24px",
        gridTemplateAreas: `"overallRating ratings"
                        "middleLeft ratings"
                        "bottomLeft ratings"`
    }
}));

const RatingDashboardScreen = () => {
    const { isDarkMode } = useExpertProfileContext();
    const { clearRatingState } = useExpertRatingContext();
    const theme = React.useMemo(() => modernTheme(isDarkMode ? "dark" : "light"), [isDarkMode]);
    const classes = useStyles(theme);

    usePageViewRecorder("ExpertRatingDashboard");

    React.useEffect(() => {
        clearRatingState("RESET_ALL");
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    padding: theme => `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(4)}`,
                    height: "calc(100vh - 90px)",
                    width: "calc(100vw - 137px)",
                    overflowY: "auto"
                }}
            >
                <RatingDashboardHeader />
                <div className={classes.container}>
                    <div style={{ gridArea: "overallRating" }}>
                        <Paper sx={{ height: "100%" }}>
                            <RatingScore />
                        </Paper>
                    </div>
                    <div style={{ gridArea: "middleLeft" }}>
                        <LearnMoreSection
                            title="Why is a high rating important?"
                            dialogTitle="Why is a high rating important?"
                            body="We highly value an exceptional rating and the positive impact it has on customer satisfaction. In the near future, we are looking at different ways to provide incentives to Experts with high ratings."
                            iconImage={<Award className={classes.icon} />}
                        />
                    </div>
                    <div style={{ gridArea: "bottomLeft" }}>
                        <LearnMoreSection
                            title="How can I improve my rating?"
                            dialogTitle="How can I improve my rating?"
                            body="You can improve your rating by reading all customer and quality reviews and taking onboard the feedback you received. Don’t forget to provide a full resolution to the consumer’s query, by adding personalized recommendations and useful tips where needed."
                            subText="To further enhance your knowledge, we encourage you to revisit our brand learning resources. Simply visit the onboarding section in your account and select the brand learning materials you wish to review. These resources will assist you in refining your skills and delivering even better customer experiences."
                            iconImage={<TrendingUp className={classes.icon} />}
                        />
                    </div>
                    <div style={{ gridArea: "ratings" }}>
                        <RatingTable theme={theme} />
                    </div>
                </div>
            </Box>
        </ThemeProvider>
    );
};

export default RatingDashboardScreen;
